// src/components/opportunity/OpportunityPricingLiveTotals.tsx

import type { OpportunityPricingTotals } from "@/types/opportunity";
import type { FormatCalcEvaluation } from "@/lib/opportunity/formatCalcEval";
import type { SalesAdjustmentsEvaluation } from "@/lib/opportunity/salesAdjustmentsEval";
import { formatOpportunityCurrency } from "@/lib/opportunity/formatCurrency";

export type OpportunityPricingLiveTotalsProps = {
  baseAmountLabel: string;
  baseAmount: number;
  formatEval: FormatCalcEvaluation | null;
  salesAdjEval: SalesAdjustmentsEvaluation | null;
  totals: OpportunityPricingTotals;
};

export function OpportunityPricingLiveTotals(props: OpportunityPricingLiveTotalsProps) {
  const { baseAmountLabel, baseAmount, formatEval, salesAdjEval, totals } = props;
  const formatLines = formatEval ? [...formatEval.before, ...formatEval.after] : [];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-2">
        Live totals
      </h4>
      <dl className="space-y-1.5 text-sm">
        <Row label={baseAmountLabel} value={formatOpportunityCurrency(baseAmount)} />
        {formatLines.map((line) => (
          <Row key={line.id} label={line.label} value={formatOpportunityCurrency(line.amount)} />
        ))}
        {salesAdjEval?.lines.map((line, idx) => (
          <Row
            key={`adj-${idx}`}
            label={line.description}
            value={formatOpportunityCurrency(line.amount)}
          />
        ))}
        <div className="border-t border-gray-100 dark:border-zinc-700 pt-2 mt-2" />
        <Row label="Subtotal" value={formatOpportunityCurrency(totals.total_cost)} />
        <Row label="Tax" value={formatOpportunityCurrency(totals.tax_amount)} />
        <Row
          label="Total sale price"
          value={formatOpportunityCurrency(totals.total_sale_price)}
          emphasis
        />
        <Row label="Deposit" value={formatOpportunityCurrency(totals.deposit_amount)} />
        <Row label="Remaining balance" value={formatOpportunityCurrency(totals.remaining_balance)} />
      </dl>
    </div>
  );
}

function Row(props: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-gray-600 dark:text-zinc-400 truncate">{props.label}</dt>
      <dd
        className={
          "tabular-nums shrink-0 " +
          (props.emphasis
            ? "text-base font-semibold text-gray-900 dark:text-zinc-100"
            : "text-gray-900 dark:text-zinc-100")
        }
      >
        {props.value}
      </dd>
    </div>
  );
}
